import { z } from 'zod'
import { CARD_COLOURS, CARD_PRIORITIES, ICONS, ITEM_TYPES, LIMITS, MOSCOW_LEVELS, STATUSES } from './constants.js'
import { relationshipsSchema, structuredContextSchema } from './context.js'
import { findingSchema } from './findings.js'
import { newId, nowIso } from './ids.js'
import { cardIntentSchema, contextReferenceSchema, INTENT_LIMITS } from './intent.js'

/** Shared entity schemas. Parsed explicitly at every boundary: import, API request, API response. */

export const uuidSchema = z.uuid()
export const isoSchema = z.iso.datetime({ offset: true })

/** Structure titles and board names are required and trimmed. */
export const titleSchema = z
  .string()
  .transform((s) => s.trim())
  .pipe(z.string().min(1, 'Title is required').max(LIMITS.title, `Maximum ${LIMITS.title} characters`))

export const cardTitleSchema = z
  .string()
  .transform((s) => s.trim())
  .pipe(z.string().min(1, 'Title is required').max(LIMITS.cardTitle, `Maximum ${LIMITS.cardTitle} characters`))

export const descriptionSchema = z.string().max(LIMITS.description, `Maximum ${LIMITS.description} characters`)

const shortTextSchema = (max: number) =>
  z
    .string()
    .transform((s) => s.trim())
    .pipe(z.string().max(max, `Maximum ${max} characters`))

/** Who is responsible: a person, role, team, service or system, as free text. */
export const ownerSchema = shortTextSchema(LIMITS.owner)
export const releaseSchema = shortTextSchema(LIMITS.release)

export const tagsSchema = z
  .array(z.string().transform((s) => s.trim()))
  .transform((tags) => {
    // Unique, order-preserving, empties dropped.
    const seen = new Set<string>()
    const out: string[] = []
    for (const tag of tags) {
      const key = tag.toLowerCase()
      if (tag.length === 0 || seen.has(key)) continue
      seen.add(key)
      out.push(tag)
    }
    return out
  })
  .pipe(
    z
      .array(z.string().max(LIMITS.tag, `Tags are limited to ${LIMITS.tag} characters`))
      .max(LIMITS.tagCount, `At most ${LIMITS.tagCount} tags`),
  )

/**
 * Where a mindmap or workflow node should land on a story map: titles, not ids, because the
 * board may not exist yet. Matched case-insensitively; empty is "not set". Defaulted, so a
 * node saved before placement existed still reads.
 */
const placementTitleSchema = shortTextSchema(LIMITS.title).default('')
const placementFieldsSchema = z.object({
  group: placementTitleSchema,
  column: placementTitleSchema,
  swimlane: placementTitleSchema,
  row: placementTitleSchema,
})
export const placementSchema = placementFieldsSchema.default({ group: '', column: '', swimlane: '', row: '' })

export type Placement = z.infer<typeof placementSchema>

export const emptyPlacement = (): Placement => ({ group: '', column: '', swimlane: '', row: '' })

export const statusSchema = z.enum(STATUSES)
/** Null is "not set". */
export const itemTypeSchema = z.enum(ITEM_TYPES).nullable().default(null)
/** Null is no icon. */
export const iconSchema = z.enum(ICONS).nullable().default(null)
/** Links to source material: stories, code, APIs, documents, tickets. Never fetched. */
export const referencesSchema = z.array(contextReferenceSchema).max(INTENT_LIMITS.referenceCount).default([])
/** Null is "not set": every existing card, legacy import and fixture parses unchanged. */
export const prioritySchema = z.enum(CARD_PRIORITIES).nullable().default(null)
export const moscowSchema = z.enum(MOSCOW_LEVELS).nullable().default(null)
export const colourSchema = z.enum(CARD_COLOURS)

/**
 * Whether a save carries its label and colour changes across the mindmap ↔ story map
 * link. Absent means both, so a client without the footer toggles (MCP) still syncs.
 */
export const linkSyncSchema = z.object({ labels: z.boolean(), colours: z.boolean() })
export type LinkSync = z.infer<typeof linkSyncSchema>

export const estimateSchema = z.number().int('Estimate must be a whole number').min(0, 'Estimate cannot be negative').nullable()

export const dueDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
  .refine((value) => {
    const date = new Date(`${value}T00:00:00.000Z`)
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value
  }, 'Not a real date')
  .nullable()

export const positionSchema = z.number().int().min(0)

/** What a board row holds: a story map (normalized structure), a mindmap (one tree) or a workflow (one graph). */
export const BOARD_KINDS = ['storymap', 'mindmap', 'workflow'] as const
export type BoardKind = (typeof BOARD_KINDS)[number]

/**
 * Structured architecture context an entity owns itself. Optional, so boards, structure and
 * cards written before it existed — including legacy imports and fixtures — parse unchanged.
 */
const contextField = structuredContextSchema.optional()

/** Notes on a heading (group, column, swimlane, row). Optional: absent reads as none. */
export const headingNotesSchema = descriptionSchema
const notesField = headingNotesSchema.optional()

/**
 * The mindmap node an entity was generated from: what label and colour sync match on.
 * Absent on everything made by hand.
 */
const sourceNodeField = uuidSchema.optional()

/**
 * How a branch maps onto a board. `columns`: the branch's children are columns and cards
 * start one level below, with groups added by hand. `groups`: the children are column
 * groups, the next level columns, and cards start below that.
 */
export const storymapLayoutSchema = z.enum(['columns', 'groups'])
export type StorymapLayout = z.infer<typeof storymapLayoutSchema>

/** Which mindmap branch, or which workflow, a generated board came from. */
export const boardSourceSchema = z
  .object({
    mindmapId: uuidSchema.optional(),
    nodeId: uuidSchema.optional(),
    workflowId: uuidSchema.optional(),
    generatedAt: isoSchema,
    /** Absent on boards generated before the choice existed, which all used `groups`. */
    layout: storymapLayoutSchema.optional(),
  })
  .refine((source) => (source.mindmapId && source.nodeId && !source.workflowId) || (source.workflowId && !source.mindmapId), {
    message: 'A board comes from a mindmap branch or from a workflow',
  })

export const boardSchema = z.object({
  id: uuidSchema,
  name: titleSchema,
  revision: z.number().int().min(0),
  createdAt: isoSchema,
  updatedAt: isoSchema,
  context: contextField,
  source: boardSourceSchema.optional(),
})

export const columnGroupSchema = z.object({
  id: uuidSchema,
  boardId: uuidSchema,
  title: titleSchema,
  notes: notesField,
  sourceNodeId: sourceNodeField,
})

export const columnSchema = z.object({
  id: uuidSchema,
  boardId: uuidSchema,
  title: titleSchema,
  groupId: uuidSchema.nullable(),
  position: positionSchema,
  context: contextField,
  notes: notesField,
  sourceNodeId: sourceNodeField,
})

export const swimlaneSchema = z.object({
  id: uuidSchema,
  boardId: uuidSchema,
  title: titleSchema,
  position: positionSchema,
  context: contextField,
  notes: notesField,
  sourceNodeId: sourceNodeField,
})

export const rowSchema = z.object({
  id: uuidSchema,
  boardId: uuidSchema,
  swimlaneId: uuidSchema,
  title: titleSchema,
  position: positionSchema,
  context: contextField,
  notes: notesField,
  sourceNodeId: sourceNodeField,
})

export const cardSchema = z.object({
  id: uuidSchema,
  boardId: uuidSchema,
  rowId: uuidSchema,
  columnId: uuidSchema,
  position: positionSchema,
  title: cardTitleSchema,
  description: descriptionSchema,
  type: itemTypeSchema,
  status: statusSchema,
  priority: prioritySchema,
  moscow: moscowSchema,
  owner: ownerSchema,
  tags: tagsSchema,
  colour: colourSchema,
  icon: iconSchema,
  estimateMinutes: estimateSchema,
  dueDate: dueDateSchema,
  release: releaseSchema,
  /**
   * Structured intent for prompt preparation. Defaulted, so cards written before this
   * existed — including legacy imports and fixtures — parse unchanged.
   */
  intent: cardIntentSchema.prefault({}),
  /** Context written on this card only; inherited context is resolved, never copied here. */
  context: contextField,
  /** Typed architectural relationships from this card to other cards on the board. */
  relationships: relationshipsSchema.optional(),
  sourceNodeId: sourceNodeField,
  /**
   * The placement its node had when the card was generated or last placed from it, so an
   * update moves the card only when that placement changes. Absent reads as none set.
   */
  sourcePlacement: placementFieldsSchema.optional(),
  /**
   * Content version, starting at 1 and increased by every saved content change — never by a
   * move. Server-assigned; absent on cards written before versioning, which read as 1.
   */
  version: z.number().int().min(1).optional(),
  createdAt: isoSchema,
  updatedAt: isoSchema,
})

/**
 * The fields every mindmap and workflow node shares with a story map card, so the three
 * read, convert and query alike. Meaning — type, status, owner, tags, relationships,
 * references — is kept apart from presentation: each kind adds its own colour, icon and
 * position. A card has the same fields; its references live in its intent.
 */
export const nodeCoreFields = {
  id: uuidSchema,
  type: itemTypeSchema,
  title: cardTitleSchema,
  description: descriptionSchema.default(''),
  /** Null is "not set". */
  status: statusSchema.nullable().default(null),
  owner: ownerSchema.default(''),
  tags: tagsSchema.default([]),
  icon: iconSchema,
  /** Typed links to other nodes on the same board. */
  relationships: relationshipsSchema.default([]),
  references: referencesSchema,
  /** Where the node lands when it becomes a story map card. */
  placement: placementSchema,
  createdAt: isoSchema,
  updatedAt: isoSchema,
}

const nodeCoreSchema = z.object(nodeCoreFields)
export type NodeCore = z.infer<typeof nodeCoreSchema>

/** The shared fields of a new node, of any kind, with nothing set but its title. */
export function newNodeCore(title: string, now: string = nowIso()): NodeCore {
  return {
    id: newId(),
    type: null,
    title,
    description: '',
    status: null,
    owner: '',
    tags: [],
    icon: null,
    relationships: [],
    references: [],
    placement: emptyPlacement(),
    createdAt: now,
    updatedAt: now,
  }
}

/** Why a node's relationships are invalid — one pointing at itself or at no node of `nodes` — or null. */
export function relationshipProblem(nodes: readonly { id: string; title: string; relationships: readonly { targetId: string }[] }[]): string | null {
  const ids = new Set(nodes.map((node) => node.id))
  for (const node of nodes) {
    for (const relationship of node.relationships) {
      if (relationship.targetId === node.id) return `"${node.title}" cannot be related to itself`
      if (!ids.has(relationship.targetId)) return `"${node.title}" is related to a node that is not on this board`
    }
  }
  return null
}

/** Wire shape for a full board: `GET /api/boards/:id` and `POST /api/boards/import`. */
export const boardSnapshotSchema = z.object({
  board: boardSchema,
  columnGroups: z.array(columnGroupSchema),
  columns: z.array(columnSchema),
  swimlanes: z.array(swimlaneSchema),
  rows: z.array(rowSchema),
  cards: z.array(cardSchema),
  /** Implementation findings. Optional, so snapshots from before findings existed parse unchanged. */
  findings: z.array(findingSchema).optional(),
})

/**
 * What the board list shows without loading a board: identity, revision, and the size of
 * the map. The counts are read from storage, never derived in the browser, because the
 * list exists precisely so a board need not be fetched.
 */
export const boardSummarySchema = boardSchema
  .pick({ id: true, name: true, revision: true, updatedAt: true })
  .extend({
    swimlaneCount: z.number().int().min(0),
    cardCount: z.number().int().min(0),
    /** Who owns it, and how the asking user reaches it. */
    ownerId: z.uuid(),
    ownerName: z.string(),
    access: z.enum(['owner', 'editor', 'viewer']),
    /** Defaulted, so a summary from before mindmaps existed reads as a story map. */
    kind: z.enum(BOARD_KINDS).default('storymap'),
    /** The mindmap a generated story map came from, when the asking user can open it. */
    sourceMindmap: z.object({ id: uuidSchema, name: z.string() }).optional(),
    /** The workflow a generated story map came from, when the asking user can open it. */
    sourceWorkflow: z.object({ id: uuidSchema, name: z.string() }).optional(),
    /** The project it belongs to. Optional only so summaries from before projects still parse. */
    projectId: uuidSchema.optional(),
  })

export type Board = z.infer<typeof boardSchema>
export type BoardSource = z.infer<typeof boardSourceSchema>
export type ColumnGroup = z.infer<typeof columnGroupSchema>
export type Column = z.infer<typeof columnSchema>
export type Swimlane = z.infer<typeof swimlaneSchema>
export type Row = z.infer<typeof rowSchema>
export type Card = z.infer<typeof cardSchema>
export type BoardSnapshot = z.infer<typeof boardSnapshotSchema>
/**
 * One page of the board list. Paging is the server's job: the browser asks for a window
 * and is told how many there are in total, rather than receiving every board and slicing
 * it locally — a tenant's list is unbounded, the viewport is not.
 */
export const boardPageSchema = z.object({
  boards: z.array(boardSummarySchema),
  /** Boards this user can reach, not boards that exist. */
  total: z.number().int().min(0),
  limit: z.number().int().min(1),
  offset: z.number().int().min(0),
})

export type BoardSummary = z.infer<typeof boardSummarySchema>
export type BoardPage = z.infer<typeof boardPageSchema>

/** What a caller may ask for. Clamped by storage, never trusted as given. */
export interface BoardPageRequest {
  limit?: number
  offset?: number
  /** Only boards of this kind; every kind when left out. */
  kind?: BoardKind
  /** Only boards in this project. */
  projectId?: string
  /** Only boards reached by a share alone, outside any project the user is in. */
  shared?: boolean
}

/** Page sizes the list offers. The first is the default. */
export const BOARD_PAGE_SIZES = [10, 25, 50] as const
export const BOARD_PAGE_LIMIT_MAX = 100

/** Editable card fields — the editor dialog commits exactly this shape. */
export const cardDraftSchema = cardSchema.pick({
  title: true,
  description: true,
  type: true,
  status: true,
  priority: true,
  moscow: true,
  owner: true,
  tags: true,
  colour: true,
  icon: true,
  estimateMinutes: true,
  dueDate: true,
  release: true,
  intent: true,
  context: true,
  relationships: true,
})

export type CardDraft = z.infer<typeof cardDraftSchema>
